import React, { useState } from 'react';
import { ArrowUpRight, ArrowDownLeft, Receipt, MessageSquare } from 'lucide-react';
import { Badge } from '@vaiu/ui/badge';
import { Button } from '@vaiu/ui/button';
import { Transaction } from '@/services/platformApi'
import { currencyService } from '@/services/currencyService';;
import { CurrencyDisplay } from '@vaiu/ui';
import TransactionDetails from '@/components/transactions/TransactionDetails';

interface ContactTransactionsProps {
  transactions: Transaction[];
  contactName: string;
}

const ContactTransactions: React.FC<ContactTransactionsProps> = ({
  transactions,
  contactName
}) => {
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const getTransactionDate = (transaction: Transaction): string | undefined => {
    // Check multiple possible date fields (per API Reference Manual)
    return transaction.date || 
           transaction.createdAt || 
           transaction.created_at || 
           transaction.dateCreated || 
           transaction.completedDate ||
           transaction.updatedAt;
  };

  const formatDate = (transaction: Transaction) => {
    const dateString = getTransactionDate(transaction);
    
    if (!dateString) {
      return "Unknown date";
    }
    
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }
    
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getTransactionDescription = (transaction: Transaction): string => {
    // Per API Reference Manual:
    // For COMPLETED transactions: message is in transaction.message (top-level)
    // For PENDING transactions: message is in transaction.data.message
    if (transaction.status === 'COMPLETED' || transaction.status === 'completed') {
      return transaction.message || transaction.description || 'No description';
    } else {
      return transaction.data?.message || transaction.message || transaction.description || 'No description';
    }
  };


  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'send':
        return <ArrowUpRight className="w-4 h-4 text-red-600" />;
      case 'receive':
        return <ArrowDownLeft className="w-4 h-4 text-green-600" />;
      default:
        return <Receipt className="w-4 h-4 text-blue-600" />;
    }
  };

  const getTransactionTitle = (transaction: Transaction) => {
    // Since we're already viewing the contact's profile, no need to repeat their name
    // Use the transaction description or a simple type-based title
    const description = getTransactionDescription(transaction);
    
    switch (transaction.type) {
      case 'send':
        return description !== 'No description' ? description : 'Payment sent';
      case 'receive':
        return description !== 'No description' ? description : 'Payment received';
      default:
        return description !== 'No description' ? description : 'Transaction';
    }
  };

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8">
        <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No transactions yet</h3>
        <p className="text-muted-foreground">
          No transaction history with {contactName}
        </p>
      </div>
    );
  }

  const handleTransactionClick = (transaction: Transaction) => {
    const transactionId = transaction.id || transaction.key || transaction._id;
    const selectedId = selectedTransaction?.id || selectedTransaction?.key || selectedTransaction?._id;
    
    // Toggle: if clicking the same transaction, close it; otherwise, select it
    if (selectedId === transactionId) {
      setSelectedTransaction(null);
    } else {
      setSelectedTransaction(transaction);
    }
  };

  const handleCloseDetails = () => {
    setSelectedTransaction(null);
  };

  return (
    <div className="space-y-4">
      <div className="divide-y divide-border rounded-lg border">
        {transactions.map((transaction, index) => {
          const transactionId = transaction.id || transaction.key || transaction._id;
          const selectedId = selectedTransaction?.id || selectedTransaction?.key || selectedTransaction?._id;
          const isSelected = selectedTransaction && selectedId === transactionId;
          
          return (
            <div key={transactionId || index}>
              <div 
                key={transaction.id} 
                className="p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => handleTransactionClick(transaction)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-muted rounded-full">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium">{getTransactionTitle(transaction)}</h4>
                        {transaction.conversationId && (
                          <MessageSquare className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      
                      {transaction.tokenKey && (
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            #{transaction.tokenKey.slice(-6)}
                          </Badge>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(transaction)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <CurrencyDisplay amount={transaction.amount}
                      currency={transaction.currency}
                      showSign={true}
                      variant={transaction.type === 'send' ? 'destructive' : 'success'}
                      className={transaction.type === 'send' ? 'text-red-600' : 'text-green-600'} currencyService={currencyService}
                    />
                  </div>
                </div>
              </div>
              
              {/* Inline Transaction Details - appears right after the clicked item */}
              {isSelected && (
                <div className="bg-slate-50/50">
                  <TransactionDetails
                    transaction={selectedTransaction}
                    onClose={handleCloseDetails}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {transactions.length >= 10 && (
        <div className="text-center">
          <Button variant="outline" size="sm">
            Load More Transactions
          </Button>
        </div>
      )}
    </div>
  );
};

export default ContactTransactions; 